import type {MethodDefWithParsedRestrictions} from '../../state/types/mthds.js';
import type {JoinedPeer} from '../../state/peers.js';
import {DomainKeys} from "../../state/types/state.js";
import {check} from '../../restrictions.js';

/**
 * Determines whether a method should be visible in the cluster based on its parsed restrictions
 * and the owning peer's identity.
 *
 * Always evaluates from a local perspective — asking "would this method, owned by this peer,
 * be visible to the cluster?" — regardless of the peer's actual source type. This ensures
 * correct filtering both when broadcasting local methods to the cluster (localRegister,
 * stateToMessages) and when receiving remote methods (registerWithConsumer).
 *
 * @param peer - The peer that owns the method. Its identity is used for restriction evaluation.
 * @param parsedRestrictions - The method's parsed restriction (e.g. 'local', 'cluster', or a complex expression).
 * @returns true if the method is cluster-visible, false if it should remain local.
 */
export function isClusterMethod(peer: JoinedPeer<DomainKeys.Agm>,
                                {parsedRestrictions}: Pick<MethodDefWithParsedRestrictions, 'parsedRestrictions'>): boolean {
    return check(parsedRestrictions, [{type: 'local'}, peer.identity], [{type: 'cluster'}, undefined]);
}
